import { useEffect, useState } from 'react';
import useLazyLoading from '../../common/hooks/loader';
import Loader from '../../components/loader';
import {
    Box, Button, Card, Divider, Grid, IconButton,
    Switch, TableBody, TableCell, TableRow, Typography
} from '@mui/material';
import { IHeaderGrid } from '../../interfaces/grid/IHeaderGrid';
import GridContent from '../../components/grid';
import GridHeader from '../../components/grid/header';
import { useNotification } from '../../components/notification/useNotification';
import extractErrorMessage from '../../common/utils/errorMessages';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { getBaseURLWithAuth } from '../../common/utils/session';
import { IIntegrationPartnerUserDto } from '../../interfaces/integration-partner/IIntegrationPartnerUserDto';
import { IntegrationPartnerLabel } from '../../common/enum/integration-partner-enum';
import {
    tryGetAllIntegrationPartners,
    tryRemoveIntegrationPartner,
    tryToggleIntegrationPartnerIsActive
} from '../../services/integration-partner/integration-partner.service';

const headerColumnList: IHeaderGrid[] = [
    { align: 'left',  name: 'Activo',   padding: 'normal' },
    { align: 'left',  name: 'Parceiro', padding: 'normal' },
    { align: 'right', name: 'Editar/Remover', padding: 'normal' },
];

export default function Integrations() {
    const navigate = useNavigate();
    const { displayNotification } = useNotification();
    const [loading, setLoading] = useState(false);
    const showLoading = useLazyLoading(loading);
    const [integrationList, setIntegrationList] = useState<IIntegrationPartnerUserDto[]>([]);

    useEffect(() => {
        async function loadIntegrations() {
            setLoading(true);
            try {
                const response = await tryGetAllIntegrationPartners(1, 50);
                if (response?.data) {
                    setIntegrationList(response.data.results ?? []);
                }
            } catch (error) {
                displayNotification({ type: 'error', message: extractErrorMessage(error) });
            } finally {
                setLoading(false);
            }
        }
        loadIntegrations();
    }, []);

    async function handleToggleIsActive(integration: IIntegrationPartnerUserDto) {
        setLoading(true);
        try {
            const response = await tryToggleIntegrationPartnerIsActive(integration.id);
            if (response?.status === 200) {
                setIntegrationList(prev =>
                    prev.map(x => x.id === integration.id ? { ...x, isActive: !x.isActive } : x)
                );
                displayNotification({ type: 'success', message: 'Integração actualizada com sucesso' });
            }
        } catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    }

    async function handleRemove(id: string) {
        setLoading(true);
        try {
            const response = await tryRemoveIntegrationPartner(id);
            if (response?.status === 202) {
                setIntegrationList(prev => prev.filter(x => x.id !== id));
                displayNotification({ type: 'success', message: 'Integração removida com sucesso' });
            }
        } catch (error) {
            displayNotification({ type: 'error', message: extractErrorMessage(error) });
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            {showLoading ? <Loader /> : null}
            <Box mt={3} mb={1}>
                <Grid container justifyContent="space-between" alignItems="flex-start">
                    <Grid>
                        <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main', letterSpacing: 1 }}>
                            Integrações
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                            Gestão das tuas API Keys de integrações externas
                        </Typography>
                    </Grid>
                    <Grid>
                        <Button variant="contained" color="primary" onClick={() => navigate(getBaseURLWithAuth() + 'integrations/create')}>
                            Nova Integração
                        </Button>
                    </Grid>
                </Grid>
                <Divider sx={{ mt: 2, borderColor: 'rgba(255,215,0,0.15)' }} />
            </Box>
            <Card sx={{ mt: 2, border: '1px solid rgba(255,215,0,0.15)' }}>
                <Box>
                    <GridContent>
                        <GridHeader headerProps={headerColumnList} />
                        <TableBody>
                            {integrationList.map((integration, index) => (
                                <TableRow
                                    key={integration.id}
                                    sx={{
                                        backgroundColor: index % 2 !== 0 ? 'rgba(255,255,255,0.02)' : 'transparent',
                                        '&:hover': { backgroundColor: 'rgba(255,215,0,0.05)' },
                                        transition: 'background-color 0.15s ease',
                                    }}
                                >
                                    <TableCell align="left">
                                        <Switch
                                            checked={integration.isActive}
                                            onChange={() => handleToggleIsActive(integration)}
                                        />
                                    </TableCell>
                                    <TableCell align="left">
                                        {IntegrationPartnerLabel[integration.partner]}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Grid container justifyContent="flex-end">
                                            <IconButton color="info" onClick={() => navigate(getBaseURLWithAuth() + `integrations/edit/${integration.id}`)}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => handleRemove(integration.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </GridContent>
                </Box>
            </Card>
        </>
    );
}
